/**
 * Stores > Store content editor at /stores/content/:id and /stores/content/new.
 *
 * Editor for one content item. Area select is locked after create. Title,
 * rich-text body (TipTap WYSIWYG, saves body_html), owner email, review date.
 * For area promotions or marketing, structured fields render: start/end date,
 * SKU chips, target-stores multi-select via SearchableSelect fed from
 * /v1/retail/stores. Attachments upload via the two-step presign flow
 * (POST /items/{id}/attachments -> PUT upload_url).
 *
 * Action bar depends on status:
 *   - draft:      Save draft, Publish (confirm), Archive
 *   - submitted:  Save draft, Approve (confirm), Reject (confirm), Archive
 *   - published:  Save draft, Archive
 *   - archived:   Save draft, Publish (confirm)
 *
 * Gated by content:admin.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Paperclip, X } from 'lucide-react';
import { FileUpload, RichTextEditor, type UploadFile } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import { useRetailStores } from '@/lib/useRetailStores';
import { storeLabel } from '@/lib/useStores';
import { uploadEditorImage } from '@/lib/editorImages';
import { withApiImageSrc } from '@/lib/richTextHtml';
import {
  CONTENT_AREAS,
  CONTENT_AREA_LABELS,
  CONTENT_STATUS_LABELS,
  hasContentAdmin,
  useAttachmentUrl,
  useContentItem,
  useContentTransition,
  useCreateContentItem,
  useRequestAttachmentUpload,
  useRequestAttachmentUploadDynamic,
  useUpdateContentItem,
  type ContentArea,
  type ContentAttachment,
  type ContentItem,
  type ContentStatus,
  type PromoFields,
  type UpsertContentBody,
} from '@/lib/useContent';
import { AccessDeniedCard, ErrorCard } from './storesShared';

const inputClass =
  'w-full rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink ' +
  'placeholder:text-amp-ink-muted focus:border-amp-olive focus:outline-none ' +
  'dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';

const btnPrimary =
  'inline-flex items-center gap-1 rounded bg-amp-olive-deep px-3 py-1.5 text-sm font-medium text-amp-cream ' +
  'hover:bg-amp-olive disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amp-olive dark:hover:bg-amp-olive-deep';
const btnSecondary =
  'inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-sm text-amp-ink ' +
  'hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-50 ' +
  'dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
const btnDanger =
  'inline-flex items-center gap-1 rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 ' +
  'hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 ' +
  'dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20';

// Areas that carry the structured promo fields (start/end date, SKUs, stores).
function isPromoArea(a: ContentArea | ''): a is 'promotions' | 'marketing' {
  return a === 'promotions' || a === 'marketing';
}

// ---------------------------------------------------------------------------
// TipTap rich-text editor
// ---------------------------------------------------------------------------


export function ContentEditor() {
  const { user } = useSession();
  const canView = hasContentAdmin(user?.permissions, user?.superAdmin);
  const params = useParams<{ id?: string }>();
  const isNew = !params.id || params.id === 'new';
  const numericId = isNew ? undefined : Number(params.id);

  const itemQ = useContentItem(isNew ? 'new' : numericId);

  if (user && !canView) return <AccessDeniedCard what="Store content" />;
  if (!isNew && itemQ.isLoading) {
    return (
      <div className="p-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden /> Loading item...
      </div>
    );
  }
  if (!isNew && itemQ.error) {
    return (
      <div className="p-6">
        <BackLink />
        <ErrorCard message={itemQ.error.message} />
      </div>
    );
  }

  return (
    <EditorForm
      key={isNew ? 'new' : String(numericId)}
      item={isNew ? null : itemQ.data?.item ?? null}
    />
  );
}

function BackLink() {
  return (
    <Link
      to="/stores/content"
      className="mb-3 inline-flex items-center gap-1 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
    >
      <ArrowLeft className="h-3 w-3" /> Back to content
    </Link>
  );
}

type FormState = {
  area: ContentArea | '';
  title: string;
  body_html: string;
  owner_email: string;
  review_date: string;
  fields: PromoFields;
};

function initialState(item: ContentItem | null): FormState {
  if (!item) {
    return {
      area: '',
      title: '',
      body_html: '',
      owner_email: '',
      review_date: '',
      fields: {},
    };
  }
  const f = item.fields ?? {};
  return {
    area: item.area,
    title: item.title,
    body_html: item.body_html ?? '',
    owner_email: item.owner_email ?? '',
    review_date: item.review_date ?? '',
    fields: {
      start_date: f.start_date ?? '',
      end_date: f.end_date ?? '',
      skus: Array.isArray(f.skus) ? f.skus : [],
      target_stores: Array.isArray(f.target_stores) ? f.target_stores : [],
    },
  };
}

function EditorForm({ item }: { item: ContentItem | null }) {
  const navigate = useNavigate();
  const isNew = item == null;
  const [state, setState] = useState<FormState>(() => initialState(item));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const createM = useCreateContentItem();
  const updateM = useUpdateContentItem(item?.id);
  const transitionM = useContentTransition(item?.id);
  const uploadDynamicM = useRequestAttachmentUploadDynamic();

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));
  const setPromo = <K extends keyof PromoFields>(key: K, value: PromoFields[K]) =>
    setState((prev) => ({ ...prev, fields: { ...prev.fields, [key]: value } }));

  const promo = isPromoArea(state.area);

  const buildBody = (): UpsertContentBody => {
    const body: UpsertContentBody = {
      title: state.title.trim(),
      body_html: state.body_html || null,
      owner_email: state.owner_email.trim() || null,
      review_date: state.review_date || null,
    };
    if (isNew && state.area) body.area = state.area as ContentArea;
    if (promo) {
      body.fields = {
        start_date: state.fields.start_date || null,
        end_date: state.fields.end_date || null,
        skus: state.fields.skus ?? [],
        target_stores: state.fields.target_stores ?? [],
      };
    }
    return body;
  };

  const validate = (): string | null => {
    if (isNew && !state.area) return 'Pick an area.';
    if (!state.title.trim()) return 'Title is required.';
    return null;
  };

  const saveDraft = async () => {
    setError(null);
    setNotice(null);
    const v = validate();
    if (v) return setError(v);
    try {
      if (isNew) {
        const res = await createM.mutateAsync({
          ...buildBody(),
          area: state.area as ContentArea,
          title: state.title.trim(),
        });
        navigate(`/stores/content/${res.item.id}`, { replace: true });
        setNotice('Draft saved.');
      } else {
        await updateM.mutateAsync(buildBody());
        setNotice('Saved.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const runTransition = async (
    action: 'publish' | 'archive' | 'approve' | 'reject',
    confirmMsg: string,
  ) => {
    if (isNew || !item) return;
    if (!window.confirm(confirmMsg)) return;
    setError(null);
    setNotice(null);
    try {
      // Save current edits first so a publish never leaves unsaved edits behind.
      await updateM.mutateAsync(buildBody());
      await transitionM.mutateAsync(action);
      setNotice(actionNoticeFor(action));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const autoCreateAndUpload = async (files: File[]) => {
    setError(null);
    setNotice(null);
    const v = validate();
    if (v) { setError(v); return; }
    let newId: number;
    try {
      const res = await createM.mutateAsync({
        ...buildBody(),
        area: state.area as ContentArea,
        title: state.title.trim(),
      });
      newId = res.item.id;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    const uploadErrors: string[] = [];
    for (const file of files) {
      try {
        const presign = await uploadDynamicM.mutateAsync({
          itemId: newId,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
        });
        const putRes = await fetch(presign.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      } catch (e) {
        uploadErrors.push(`${file.name}: ${e instanceof Error ? e.message : 'Upload failed'}`);
      }
    }
    // Navigate to the item URL; any failed files can be retried on the item page.
    navigate(`/stores/content/${newId}`, { replace: true });
  };

  const status: ContentStatus = item?.status ?? 'draft';
  const pending = createM.isPending || updateM.isPending || transitionM.isPending || uploadDynamicM.isPending;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col p-4">
      <BackLink />
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Stores · Content
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            {isNew ? 'New page' : state.title || '(untitled)'}
          </h1>
          {!isNew && item && (
            <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Status: <strong>{CONTENT_STATUS_LABELS[status]}</strong>
              {item.store_no != null && ` · Submitted by store ${item.store_no}`}
              {item.published_at && ` · Published ${formatDate(item.published_at)}`}
              {item.updated_at && ` · Updated ${formatDateTime(item.updated_at)}`}
            </p>
          )}
        </div>
      </header>

      {notice && (
        <p className="mb-3 rounded-md bg-green-100 px-3 py-2 text-sm text-green-800 dark:bg-green-900/40 dark:text-green-300">
          {notice}
        </p>
      )}
      {error && (
        <p className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <FilterLabel htmlFor="content-area">Area</FilterLabel>
            <select
              id="content-area"
              value={state.area}
              disabled={!isNew}
              onChange={(e) => setField('area', e.target.value as ContentArea)}
              className={`${inputClass} ${!isNew ? 'opacity-60' : ''}`}
              title={!isNew ? 'Area is locked after create.' : undefined}
            >
              <option value="">Pick an area…</option>
              {CONTENT_AREAS.map((a) => (
                <option key={a} value={a}>
                  {CONTENT_AREA_LABELS[a]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FilterLabel htmlFor="content-title">Title</FilterLabel>
            <input
              id="content-title"
              type="text"
              value={state.title}
              onChange={(e) => setField('title', e.target.value)}
              className={inputClass}
              placeholder="e.g. Sweet Spot Summer Promo"
            />
          </div>
          <div>
            <FilterLabel htmlFor="content-owner">Owner email</FilterLabel>
            <input
              id="content-owner"
              type="email"
              value={state.owner_email}
              onChange={(e) => setField('owner_email', e.target.value)}
              className={inputClass}
              placeholder="owner@ampersandbrands.com"
            />
          </div>
          <div>
            <FilterLabel htmlFor="content-review">Review by</FilterLabel>
            <input
              id="content-review"
              type="date"
              value={state.review_date}
              onChange={(e) => setField('review_date', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="space-y-3">
          {promo && (
            <PromoFieldsBlock
              fields={state.fields}
              onChange={setPromo}
            />
          )}
        </div>
      </div>

      <div className="mt-4">
        <FilterLabel as="div">Body</FilterLabel>
        <RichTextEditor
          value={withApiImageSrc(state.body_html)}
          onChange={(html) => setField('body_html', html)}
          onInsertImage={uploadEditorImage}
          aria-label="Body"
          className="min-h-[16rem]"
        />
      </div>

      <AttachmentsBlock
        item={item}
        onPickFilesNew={isNew ? autoCreateAndUpload : undefined}
      />

      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-amp-line pt-4 dark:border-amp-dark-line">
        <button type="button" onClick={saveDraft} disabled={pending} className={btnPrimary}>
          {pending && <Loader2 className="h-3 w-3 animate-spin" />}
          {isNew ? 'Create draft' : 'Save draft'}
        </button>

        {!isNew && item && status === 'submitted' && (
          <>
            <button
              type="button"
              disabled={pending}
              onClick={() =>
                runTransition(
                  'approve',
                  `Approve and publish "${state.title}"?${
                    item.store_no != null ? ` Submitted by store ${item.store_no}.` : ''
                  }`,
                )
              }
              className={btnPrimary}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() =>
                runTransition(
                  'reject',
                  `Reject "${state.title}"? It will be archived.${
                    item.store_no != null ? ` Submitted by store ${item.store_no}.` : ''
                  }`,
                )
              }
              className={btnDanger}
            >
              Reject
            </button>
          </>
        )}

        {!isNew && item && (status === 'draft' || status === 'archived') && (
          <button
            type="button"
            disabled={pending}
            onClick={() => runTransition('publish', `Publish "${state.title}"?`)}
            className={btnSecondary}
          >
            Publish
          </button>
        )}

        {!isNew && item && status !== 'archived' && (
          <button
            type="button"
            disabled={pending}
            onClick={() => runTransition('archive', `Archive "${state.title}"?`)}
            className={btnSecondary}
          >
            Archive
          </button>
        )}
      </div>
    </div>
  );
}

function actionNoticeFor(a: 'publish' | 'archive' | 'approve' | 'reject'): string {
  switch (a) {
    case 'publish':
      return 'Published.';
    case 'archive':
      return 'Archived.';
    case 'approve':
      return 'Approved and published.';
    case 'reject':
      return 'Rejected and archived.';
  }
}

// ---------------------------------------------------------------------------
// Promo (promotions / marketing) structured fields
// ---------------------------------------------------------------------------

function PromoFieldsBlock({
  fields,
  onChange,
}: {
  fields: PromoFields;
  onChange: <K extends keyof PromoFields>(key: K, value: PromoFields[K]) => void;
}) {
  const storesQ = useRetailStores();
  const storeOptions: SearchableSelectOption[] = useMemo(
    () =>
      [...storesQ.stores]
        .sort((a, b) => String(a.store_no).localeCompare(String(b.store_no), undefined, { numeric: true }))
        .map((s) => ({
          value: String(s.store_no),
          label: storeLabel(s.store_no, s.store_name),
          sublabel: [s.city, s.state].filter(Boolean).join(', ') || undefined,
        })),
    [storesQ.stores],
  );

  return (
    <div className="rounded-md border border-amp-line p-3 dark:border-amp-dark-line">
      <p className="mb-3 text-xs font-semibold uppercase tracking-caps-pill text-amp-ink dark:text-amp-dark-ink">
        Promo details
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <FilterLabel htmlFor="promo-start">Start date</FilterLabel>
          <input
            id="promo-start"
            type="date"
            value={fields.start_date ?? ''}
            onChange={(e) => onChange('start_date', e.target.value || null)}
            className={inputClass}
          />
        </div>
        <div>
          <FilterLabel htmlFor="promo-end">End date</FilterLabel>
          <input
            id="promo-end"
            type="date"
            value={fields.end_date ?? ''}
            onChange={(e) => onChange('end_date', e.target.value || null)}
            className={inputClass}
          />
        </div>
      </div>
      <div className="mt-3">
        <FilterLabel as="div">SKUs</FilterLabel>
        <SkuChipsInput
          values={fields.skus ?? []}
          onChange={(next) => onChange('skus', next)}
        />
      </div>
      <div className="mt-3">
        <FilterLabel as="div">Target stores</FilterLabel>
        <SearchableSelect
          options={storeOptions}
          selected={fields.target_stores ?? []}
          onChange={(next) => onChange('target_stores', next)}
          multiSelect
          placeholder="All stores"
          ariaLabel="Target stores"
        />
        {storesQ.error && (
          <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Store list unavailable ({storesQ.error.message}). Leave empty to target all stores.
          </p>
        )}
      </div>
    </div>
  );
}

// Simple chips input: type + Enter (or comma) to add a SKU; click X to remove.
function SkuChipsInput({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const parts = draft.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...values];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraft('');
  };

  const removeAt = (i: number) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-md border border-amp-line px-2 py-1.5 dark:border-amp-dark-line">
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v, i) => (
          <Pill key={`${v}-${i}`}>
            <span>{v}</span>
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${v}`}
              className="ml-1 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
            >
              <X className="h-3 w-3" />
            </button>
          </Pill>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && !draft && values.length > 0) {
              removeAt(values.length - 1);
            }
          }}
          onBlur={commit}
          placeholder={values.length === 0 ? 'Type a SKU and press Enter' : ''}
          className="flex-1 min-w-[120px] border-0 bg-transparent text-sm text-amp-ink placeholder:text-amp-ink-muted focus:outline-none dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

function AttachmentsBlock({
  item,
  onPickFilesNew,
}: {
  item: ContentItem | null;
  onPickFilesNew?: (files: File[]) => Promise<void>;
}) {
  const requestM = useRequestAttachmentUpload(item?.id);
  const [uploading, setUploading] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  // Files sitting in the picker between being chosen and finishing their
  // upload. Cleared as soon as the upload settles.
  const [staged, setStaged] = useState<UploadFile[]>([]);

  const handleFiles = async (files: File[]) => {
    setFileErrors([]);
    if (!item) {
      if (!onPickFilesNew) return;
      setUploading(true);
      try {
        await onPickFilesNew(files);
      } finally {
        setUploading(false);
      }
      return;
    }
    setUploading(true);
    const errors: string[] = [];
    for (const file of files) {
      try {
        const { upload_url } = await requestM.mutateAsync({
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
        });
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      } catch (e) {
        errors.push(`${file.name}: ${e instanceof Error ? e.message : 'Upload failed'}`);
      }
    }
    setUploading(false);
    setFileErrors(errors);
  };

  return (
    <div className="mt-6 rounded-md border border-amp-line p-3 dark:border-amp-dark-line">
      <div className="mb-2 flex items-center gap-2">
        <Paperclip className="h-3 w-3 text-amp-ink-muted dark:text-amp-dark-ink-muted" aria-hidden />
        <p className="text-xs font-semibold uppercase tracking-caps-pill text-amp-ink dark:text-amp-dark-ink">
          Attachments
        </p>
      </div>
      {/* The shared picker stages files; Cockpit uploads them the moment they
          are chosen, exactly as the old button did, then clears the staging
          list so the saved attachment list below stays the one source of
          truth. Drag-and-drop and paste come along for free. */}
      <div className="mb-3">
        <FileUpload
          files={staged}
          onChange={(next: UploadFile[]) => {
            if (next.length === 0) return;
            setStaged(next);
            void handleFiles(next.map((f) => f.file)).finally(() => setStaged([]));
          }}
          multiple
          label={uploading ? 'Uploading...' : 'Add files'}
          disabled={uploading}
        />
      </div>
      {fileErrors.map((err) => (
        <p key={err} className="mb-2 text-sm text-red-700 dark:text-red-300">{err}</p>
      ))}
      {!item ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Save a draft first to attach files, or pick files above to auto-save and attach.
        </p>
      ) : item.attachments.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          No attachments yet.
        </p>
      ) : (
        <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {item.attachments.map((a) => (
            <AttachmentRow key={a.id} itemId={item.id} attachment={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachmentRow({
  itemId,
  attachment,
}: {
  itemId: number;
  attachment: ContentAttachment;
}) {
  // Presigned GET is fetched on demand when the user clicks Open.
  const [wantUrl, setWantUrl] = useState(false);
  const { data, isLoading, error } = useAttachmentUrl(
    wantUrl ? itemId : undefined,
    wantUrl ? attachment.id : undefined,
  );

  useEffect(() => {
    if (wantUrl && data?.url) {
      window.open(data.url, '_blank', 'noopener');
      setWantUrl(false);
    }
  }, [wantUrl, data?.url]);

  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <span className="min-w-0 flex-1 truncate text-amp-ink dark:text-amp-dark-ink">
        <Paperclip className="mr-1.5 inline h-3 w-3 text-amp-ink-muted" aria-hidden />
        {attachment.filename}
        <span className="ml-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {attachment.content_type}
        </span>
      </span>
      <button type="button" onClick={() => setWantUrl(true)} className={btnSecondary}>
        {isLoading && wantUrl ? 'Opening...' : 'Open'}
      </button>
      {error && (
        <span className="text-xs text-red-700 dark:text-red-300">{error.message}</span>
      )}
    </li>
  );
}
